<template>
  <div class="admin-page">
    <!-- ── 页头 ───────────────────────────────────────────── -->
    <div class="page-head">
      <div class="head-text">
        <h1>认证审核</h1>
        <p>学籍材料的人工复核，通过后自动删除证件影像</p>
      </div>
      <div class="head-actions">
        <el-button class="ghost-btn" icon="el-icon-refresh" @click="fetchList">刷新</el-button>
      </div>
    </div>

    <!-- ── 指标（可点击筛选） ─────────────────────────────── -->
    <div class="stat-row">
      <div
        class="stat-card is-clickable"
        :class="{ 'is-warn': query.status === 1, 'is-active': query.status === 1 }"
        @click="quickFilter(1)"
      >
        <div class="stat-num">{{ stats.pendingCount || 0 }}</div>
        <div class="stat-label">待审核</div>
        <div class="stat-sub">需要尽快处理</div>
      </div>
      <div
        class="stat-card is-clickable"
        :class="{ 'stat-gold': query.status === 2, 'is-active': query.status === 2 }"
        @click="quickFilter(2)"
      >
        <div class="stat-num"> {{ stats.verifiedCount || 0 }}</div>
        <div class="stat-label">已认证</div>
        <div class="stat-sub">累计通过</div>
      </div>
      <div
        class="stat-card is-clickable"
        :class="{ 'is-active': query.status === 3 }"
        @click="quickFilter(3)"
      >
        <div class="stat-num">{{ stats.rejectedCount || 0 }}</div>
        <div class="stat-label">已驳回</div>
        <div class="stat-sub">用户可重新提交</div>
      </div>
      <div
        class="stat-card is-clickable"
        :class="{ 'is-active': query.status === 0 }"
        @click="quickFilter(0)"
      >
        <div class="stat-num">{{ total }}</div>
        <div class="stat-label">全部记录</div>
        <div class="stat-sub">含未提交</div>
      </div>
    </div>

    <!-- ── 说明 ───────────────────────────────────────────── -->
    <div class="hint-bar verify-hint">
      <i class="el-icon-info" />
      <span>
        <b>学生认证为可选功能。</b>未认证用户可正常使用全部功能。认证仅用于给资料增加权威标识与每日额外免费取出次数，审核时请重点确认照片上的学校、姓名与人脸是否清晰可辨。审核通过后系统会自动删除学生证照片，不长期留存证件影像。
      </span>
    </div>

    <!-- ── 筛选 ───────────────────────────────────────────── -->
    <div class="filter-bar">
      <el-input
        v-model="query.keyword"
        placeholder="搜索昵称 / 学号 / 真实姓名"
        clearable
        prefix-icon="el-icon-search"
        class="search-input verify-search"
        @keyup.enter.native="handleSearch"
        @clear="handleSearch"
      />
      <el-select v-model="query.status" placeholder="全部状态" class="status-select" @change="handleSearch">
        <el-option label="待审核" :value="1" />
        <el-option label="已认证" :value="2" />
        <el-option label="已驳回" :value="3" />
        <el-option label="全部" :value="0" />
      </el-select>
      <el-button type="primary" class="primary-btn" @click="handleSearch">查询</el-button>
      <el-button class="ghost-btn" @click="resetQuery">重置</el-button>
      <div class="filter-tail">共 <b>{{ total }}</b> 条</div>
    </div>

    <!-- ── 列表 ───────────────────────────────────────────── -->
    <div class="table-card">
      <el-table v-loading="loading" :data="list" style="width: 100%">
        <el-table-column label="用户" min-width="200">
          <template slot-scope="{ row }">
            <div class="owner-cell">
              <img class="owner-avatar verify-avatar" :src="row.avatar || defaultAvatar" />
              <div class="owner-info">
                <div class="owner-name">{{ row.nickname || '未设置昵称' }}</div>
                <div class="owner-school">ID {{ row.id }}</div>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="学校" min-width="160">
          <template slot-scope="{ row }">
            <span class="cell-main">{{ row.schoolName || '—' }}</span>
          </template>
        </el-table-column>

        <el-table-column label="学号 / 姓名" min-width="150">
          <template slot-scope="{ row }">
            <div class="cell-main">{{ row.verifyStudentNo || '—' }}</div>
            <div v-if="row.verifyRealName" class="cell-sub">{{ row.verifyRealName }}</div>
          </template>
        </el-table-column>

        <el-table-column label="提交时间" width="170">
          <template slot-scope="{ row }">
            <span class="cell-sub">{{ row.verifySubmittedAt || '—' }}</span>
          </template>
        </el-table-column>

        <el-table-column label="状态" width="120">
          <template slot-scope="{ row }">
            <span class="status-pill" :class="statusPillClass(row.verifyStatus)">
              <i />{{ statusText(row.verifyStatus) }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="230" fixed="right">
          <template slot-scope="{ row }">
            <div class="row-actions">
              <button
                v-if="row.verifyImage"
                type="button"
                class="act act-plain"
                @click="viewMaterial(row)"
              >
                查看材料
              </button>
              <template v-if="row.verifyStatus === 1">
                <button type="button" class="act act-primary" @click="approve(row)">通过</button>
                <button type="button" class="act act-danger" @click="reject(row)">驳回</button>
              </template>
              <span v-else-if="row.verifyStatus === 2" class="row-done">已通过</span>
              <span v-else class="row-done">{{ row.verifyRemark || '已驳回' }}</span>
            </div>
          </template>
        </el-table-column>

        <template slot="empty">
          <div class="empty-state">
            <i class="el-icon-document-checked" />
            <p>暂无认证记录，用户可在小程序「我的 → 学生认证」中提交材料</p>
          </div>
        </template>
      </el-table>

      <div class="pager">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :total="total"
          :current-page.sync="query.page"
          :page-size.sync="query.size"
          :page-sizes="[10, 20, 50]"
          @current-change="fetchList"
          @size-change="handleSearch"
        />
      </div>
    </div>

    <!-- ── 材料查看 ───────────────────────────────────────── -->
    <el-dialog title="认证材料" :visible.sync="dialogVisible" width="560px">
      <div class="material">
        <div class="material__imgbox" v-if="currentRow.verifyImage">
          <img class="material__img" :src="currentRow.verifyImage" />
        </div>
        <div class="material__meta">
          <div class="material__row"><span class="material__label">昵称</span><span>{{ currentRow.nickname || '—' }}</span></div>
          <div class="material__row"><span class="material__label">学校</span><span>{{ currentRow.schoolName || '—' }}</span></div>
          <div class="material__row"><span class="material__label">学号</span><span>{{ currentRow.verifyStudentNo || '未填写' }}</span></div>
          <div class="material__row"><span class="material__label">真实姓名</span><span>{{ currentRow.verifyRealName || '未填写' }}</span></div>
          <div class="material__row"><span class="material__label">提交时间</span><span>{{ currentRow.verifySubmittedAt || '—' }}</span></div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button class="ghost-btn" @click="dialogVisible = false">关闭</el-button>
        <el-button v-if="currentRow.verifyStatus === 1" type="danger" class="danger-btn" @click="reject(currentRow)">驳回</el-button>
        <el-button v-if="currentRow.verifyStatus === 1" type="primary" class="primary-btn" @click="approve(currentRow)">通过认证</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getVerifyList, reviewVerify } from '@/api/verify'

export default {
  name: 'VerifyAudit',
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      stats: {
        pendingCount: 0,
        verifiedCount: 0,
        rejectedCount: 0
      },
      query: {
        page: 1,
        size: 10,
        status: 1,
        keyword: ''
      },
      dialogVisible: false,
      currentRow: {},
      defaultAvatar: 'https://xdmh.xbjava.com/static/default-avatar.png'
    }
  },
  created() {
    // 深链：钉钉消息带 ?focus=<用户ID>。认证列表按提交时间倒序（新的在前），
    // 所以刚提交的那条一定在第 1 页，直接在已加载列表里找即可。
    this.fetchList().then(() => this.applyFocus())
  },
  methods: {
    fetchList() {
      this.loading = true
      const params = {
        page: this.query.page,
        size: this.query.size,
        keyword: this.query.keyword
      }
      // status 为 0 表示"全部"，不传该参数
      if (this.query.status) {
        params.status = this.query.status
      }
      getVerifyList(params)
        .then(res => {
          const data = res.data || {}
          this.list = data.list || []
          this.total = data.total || 0
          this.stats = {
            pendingCount: data.pendingCount || 0,
            verifiedCount: data.verifiedCount || 0,
            rejectedCount: data.rejectedCount || 0
          }
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleSearch() {
      this.query.page = 1
      this.fetchList()
    },
    resetQuery() {
      this.query = { page: 1, size: 10, status: 1, keyword: '' }
      this.fetchList()
    },
    quickFilter(status) {
      this.query.status = status
      this.handleSearch()
    },
    applyFocus() {
      const id = Number(this.$route.query.focus)
      if (!id) return
      const row = this.list.find(item => item.id === id)
      if (row) this.viewMaterial(row)
      const query = { ...this.$route.query }
      delete query.focus
      this.$router.replace({ path: this.$route.path, query }).catch(() => {})
    },
    viewMaterial(row) {
      this.currentRow = row
      this.dialogVisible = true
    },
    approve(row) {
      this.$confirm(
        `确认通过「${row.nickname || '该用户'}」的学生认证？通过后对方资料页会显示认证标识，并每天多获得 1 次免费取出机会。学生证照片将被自动删除。`,
        '通过认证',
        { confirmButtonText: '确认通过', cancelButtonText: '取消', type: 'warning' }
      ).then(() => {
        reviewVerify({ userId: row.id, approved: true }).then(() => {
          this.$message.success('已通过认证')
          this.dialogVisible = false
          this.fetchList()
        })
      }).catch(() => {})
    },
    reject(row) {
      this.$prompt('驳回原因会展示给用户，请写清楚需要补充或重拍什么', '驳回认证', {
        confirmButtonText: '确认驳回',
        cancelButtonText: '取消',
        inputPlaceholder: '例如：学生证照片模糊，看不清学号',
        inputValidator: (value) => {
          if (!value || !value.trim()) {
            return '请填写驳回原因'
          }
          if (value.length > 100) {
            return '原因请控制在 100 字以内'
          }
          return true
        }
      }).then(({ value }) => {
        reviewVerify({ userId: row.id, approved: false, remark: value.trim() }).then(() => {
          this.$message.success('已驳回')
          this.dialogVisible = false
          this.fetchList()
        })
      }).catch(() => {})
    },
    statusText(status) {
      if (status === 1) return '待审核'
      if (status === 2) return '已认证'
      if (status === 3) return '已驳回'
      return '未提交'
    },
    statusPillClass(status) {
      if (status === 1) return 's0'
      if (status === 2) return 'is-gold'
      return 's2'
    }
  }
}
</script>

<style lang="scss" scoped>
/* 可点击的统计卡：选中时描边加深，充当筛选器 */
.stat-card.is-clickable {
  cursor: pointer;
  user-select: none;
}

.stat-card.is-clickable:hover {
  border-color: var(--ink-300);
}

.stat-card.is-clickable.is-active {
  border-color: var(--rose-400);
  box-shadow: 0 0 0 3px var(--rose-50);
}

.stat-card.stat-gold {
  border-top-color: var(--gold);
}

.stat-card.stat-gold .stat-num {
  color: var(--gold);
}

.stat-card.stat-gold.is-clickable.is-active {
  border-color: var(--gold);
  box-shadow: 0 0 0 3px #fbf3dc;
}

.verify-hint {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 14px;
  line-height: 1.7;
}

.verify-hint i {
  margin-top: 2px;
  flex: 0 0 auto;
}

.verify-search {
  width: 280px;
}

.status-select {
  width: 150px;
}

.verify-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
}

.row-done {
  font-size: 12.5px;
  color: var(--ink-400);
}

.danger-btn {
  color: #fff;
  background: var(--rose-600);
  border-color: var(--rose-600);
}

.danger-btn:hover,
.danger-btn:focus {
  background: var(--rose-500);
  border-color: var(--rose-500);
  color: #fff;
}

.material__imgbox {
  background: var(--ink-100);
  border-radius: var(--r-lg);
  padding: 10px;
  text-align: center;
}

.material__img {
  max-width: 100%;
  max-height: 340px;
  border-radius: var(--r-md);
}

.material__meta {
  margin-top: 16px;
}

.material__row {
  display: flex;
  font-size: 13px;
  line-height: 2;
  color: var(--ink-900);
}

.material__label {
  width: 90px;
  flex-shrink: 0;
  color: var(--ink-400);
}
</style>
